import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { calculateLoadCalcSheet, deleteCalcSheet, getCalcSheet, updateCalcSheet } from "../../api/calc";
import type { CalcInputs, CalcSheet } from "../../types/calc";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { HelpTip } from "../common/HelpTip";
import { useToast } from "../common/Toast";
import {
  LOAD_CALC_TEMPLATE_BY_ID,
  defaultLoadCalcValues,
  isLoadCalcTemplateId,
  loadTemplateIdFromCalculatorId,
} from "./loadCalcDefinitions";
import type {
  LoadCalcField,
  LoadCalcRow,
  LoadCalcResultPayload,
  LoadCalcTemplate,
  LoadCalcValue,
  LoadCalcValues,
} from "./loadCalcTypes";
import type { AutomationSurfacePath } from "./loadCalcAutomationAdapter";
import { useCalcProject } from "./CalcProjectContext";
import { NdsCoverBlock } from "./NdsCoverBlock";
import { WallAssemblyPicker, type WallAssemblyLayer } from "./WallAssemblyPicker";
import { useCalcExport } from "./export/CalcExportWizard";
import {
  CalcDisclosure,
  CalcInputCard,
  CalcMetricStrip,
  CalcNoticeList,
  CalcResultPanel,
  CalcSheetHeader,
  type CalcSheetNotice,
  type CalcSheetTone,
} from "./CalcSheetUi";

import "../../styles/nds-calc.css";
import "../../styles/load-calc.css";
import "../../styles/catalog-lookup.css";

function numberValue(values: LoadCalcValues, key: string, fallback: number) {
  const raw = values[key];
  if (raw === "" || raw === undefined || raw === null || Array.isArray(raw)) return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

function displayNumber(values: LoadCalcValues, key: string, fallback: number, unknownMissing: boolean) {
  const raw = values[key];
  if (unknownMissing && (raw === "" || raw === undefined || raw === null)) return "—";
  return numberValue(values, key, fallback);
}

function textValue(values: LoadCalcValues, key: string, fallback: string) {
  const value = values[key];
  return value === undefined || value === "" || Array.isArray(value) ? fallback : String(value);
}

function firstRow(values: LoadCalcValues, key: string): LoadCalcRow | undefined {
  const value = values[key];
  return Array.isArray(value) ? value[0] : undefined;
}

function upgradeLegacyLoadValues(templateId: string, supplied?: LoadCalcValues): LoadCalcValues {
  const next = { ...(supplied ?? {}) };
  if (templateId === "wind_cc" && !Array.isArray(next.components) && next.component !== undefined) {
    next.components = [{ component: String(next.component), effectiveArea_sqft: Number(next.effectiveArea_sqft ?? 10) }];
  }
  if (templateId === "snow" && !Array.isArray(next.roofPlanes) && next.roofPlaneId !== undefined) {
    next.adjacencies = [];
    next.obstructions = [];
    next.roofPlanes = [{
      roofPlaneId: String(next.roofPlaneId), roofLength_ft: Number(next.roofLength_ft ?? 42),
      eaveToRidge_ft: Number(next.eaveToRidge_ft ?? 16), roofPitch: Number(next.roofPitch ?? 6),
      roofShape: String(next.roofShape ?? "Gable"), simplySupported: Boolean(next.simplySupported ?? true),
    }];
    if (Number(next.stepHeight_ft ?? 0) > 0) {
      const upperId = `${String(next.roofPlaneId)} upper`;
      next.roofPlanes = [...next.roofPlanes, {
        roofPlaneId: upperId, roofLength_ft: Number(next.roofLength_ft ?? 42),
        eaveToRidge_ft: Number(next.upperFetch_ft ?? 20), roofPitch: Number(next.roofPitch ?? 6),
        roofShape: String(next.roofShape ?? "Gable"), simplySupported: Boolean(next.simplySupported ?? true),
      }];
      next.adjacencies = [{
        adjacencyId: "Legacy roof step", upperRoofPlaneId: upperId,
        lowerRoofPlaneId: String(next.roofPlaneId), stepHeight_ft: Number(next.stepHeight_ft),
        upperFetch_ft: Number(next.upperFetch_ft ?? 20), lowerFetch_ft: Number(next.lowerFetch_ft ?? 20),
      }];
    }
    if (Number(next.obstructionHeight_ft ?? 0) > 0) next.obstructions = [{
      obstructionId: "Legacy obstruction", hostRoofPlaneId: String(next.roofPlaneId),
      height_ft: Number(next.obstructionHeight_ft), upwindFetch_ft: Number(next.lowerFetch_ft ?? next.eaveToRidge_ft ?? 20),
    }];
  }
  if (templateId === "seismic_elf" && !Array.isArray(next.stories) && next.storyCount !== undefined) {
    const count = Math.max(1, Math.round(Number(next.storyCount)));
    const height = Number(next.totalHeight_ft ?? count * 10);
    const weight = Number(next.totalWeight_lb ?? count * 80000);
    next.stories = Array.from({ length: count }, (_, index) => ({
      storyLabel: index === count - 1 ? "Roof" : `Level ${index + 1}`,
      height_ft: height * (index + 1) / count, weight_lb: weight / count,
    }));
    next.axes = [{
      axis: String(next.axis ?? "X"), sfrs: String(next.sfrs ?? "Light-frame wood shear walls"),
      R: Number(next.R ?? 6.5), Cd: Number(next.Cd ?? 4), omega0: Number(next.omega0 ?? 3),
      periodMode: String(next.periodMode ?? "Approximate Ta"), period_seconds: Number(next.period_seconds ?? .3),
    }];
  }
  return next;
}

function TableFieldControl({ field, value, onChange, onCommit, disabled }: {
  field: LoadCalcField;
  value: LoadCalcValue | undefined;
  onChange: (value: LoadCalcValue) => void;
  onCommit: (value?: LoadCalcValue) => void;
  disabled: boolean;
}) {
  const rows = Array.isArray(value) ? value : [];
  const columns = field.columns ?? [];
  const minimumRows = field.minimumRows ?? 0;
  const addRow = () => {
    const next: LoadCalcRow = {};
    for (const column of columns) next[column.key] = column.value ?? (column.type === "bool" ? false : "");
    const nextRows = [...rows, next];
    onChange(nextRows);
    onCommit(nextRows);
  };
  const updateCell = (rowIndex: number, key: string, nextValue: string | number | boolean, commit = false) => {
    const nextRows = rows.map((row, index) => index === rowIndex ? { ...row, [key]: nextValue } : row);
    onChange(nextRows);
    if (commit) onCommit(nextRows);
  };
  return (
    <div className="lc-table-field" title={field.help}>
      <div className="lc-table-field-head">
        <span>{field.label}</span>
        {!disabled ? <button type="button" className="nds-btn-ghost" onClick={addRow}>{field.addLabel ?? "Add row"}</button> : null}
      </div>
      <div className="lc-table-input-scroll">
        <table className="calc-sheet-table">
          <thead><tr>{columns.map((column) => <th key={column.key}>{column.label}{column.unit ? <small>{column.unit}</small> : null}</th>)}{!disabled ? <th aria-label="Row actions" /> : null}</tr></thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((column) => {
                  const cell = row[column.key];
                  if (column.type === "bool") return <td key={column.key}><input type="checkbox" disabled={disabled} checked={Boolean(cell)} onChange={(event) => updateCell(rowIndex, column.key, event.target.checked, true)} /></td>;
                  if (column.type === "select") return (
                    <td key={column.key}><select disabled={disabled} value={String(cell ?? "")} onChange={(event) => updateCell(rowIndex, column.key, event.target.value, true)}>
                      {(column.options ?? []).map((option) => <option key={option}>{option}</option>)}
                    </select></td>
                  );
                  return <td key={column.key}><input type={column.type} disabled={disabled} value={cell === undefined ? "" : String(cell)} onChange={(event) => updateCell(rowIndex, column.key, column.type === "number" && event.target.value !== "" ? Number(event.target.value) : event.target.value)} onBlur={() => onCommit()} /></td>;
                })}
                {!disabled ? <td><button type="button" className="lc-row-remove" disabled={rows.length <= minimumRows} aria-label={`Remove ${field.label} row ${rowIndex + 1}`} onClick={() => { const nextRows = rows.filter((_, index) => index !== rowIndex); onChange(nextRows); onCommit(nextRows); }}>×</button></td> : null}
              </tr>
            ))}
            {!rows.length ? <tr><td className="lc-table-empty" colSpan={columns.length + (disabled ? 0 : 1)}>No rows entered.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FieldControl({ field, value, onChange, onCommit, disabled = false }: {
  field: LoadCalcField;
  value: LoadCalcValues[string] | undefined;
  onChange: (value: LoadCalcValue) => void;
  onCommit: (value?: LoadCalcValue) => void;
  disabled?: boolean;
}) {
  if (field.type === "table") return <TableFieldControl field={field} value={value} onChange={onChange} onCommit={onCommit} disabled={disabled} />;
  if (field.type === "bool") {
    const unknown = typeof value !== "boolean";
    return (
      <label className={`nds-field nds-field-min lc-field lc-toggle-field${field.wide ? " is-wide" : ""}`} title={field.help}>
        <span className="nds-field-label lc-field-label">{field.label}</span>
        {disabled && unknown ? <span className="lc-unknown-value">—</span> : (
          <button
            type="button"
            className={`lc-switch${value ? " is-on" : ""}`}
            role="switch"
            aria-checked={Boolean(value)}
            disabled={disabled}
            onClick={() => {
              onChange(!value);
              onCommit(!value);
            }}
          >
            <span />
          </button>
        )}
      </label>
    );
  }

  return (
    <label className={`nds-field nds-field-min lc-field${field.wide ? " is-wide" : ""}`} title={field.help}>
      <span className="nds-field-label lc-field-label">
        <span>{field.label}</span>
        {field.unit ? <small>{field.unit}</small> : null}
      </span>
      {field.type === "select" ? (
        <select
          className="nds-input"
          disabled={disabled}
          value={String(value ?? "")}
          onChange={(event) => {
            onChange(event.target.value);
            onCommit(event.target.value);
          }}
        >
          {value === "" || value === undefined ? <option value="">—</option> : null}
          {(field.options ?? []).map((option) => <option key={option}>{option}</option>)}
        </select>
      ) : (
        <div className="nds-input-affix lc-input-wrap">
          <input
            type={field.type}
            className={field.type === "number" ? "num nds-input" : "nds-input"}
            disabled={disabled}
            value={value === undefined ? "" : String(value)}
            onChange={(event) => onChange(field.type === "number" && event.target.value !== "" ? Number(event.target.value) : event.target.value)}
            onBlur={() => onCommit()}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
            }}
          />
          {field.unit ? <span className="nds-input-suffix">{field.unit}</span> : null}
        </div>
      )}
    </label>
  );
}

function ArrowDefs() {
  return (
    <defs>
      <marker id="lc-arrow" markerWidth="7" markerHeight="7" refX="5.5" refY="3.5" orient="auto">
        <path d="M0 0 7 3.5 0 7z" fill="currentColor" />
      </marker>
      <pattern id="lc-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="8" stroke="currentColor" strokeOpacity=".18" strokeWidth="2" />
      </pattern>
    </defs>
  );
}

function pathGeometry(geometry: AutomationSurfacePath[]) {
  const points = geometry.flatMap((path) => path.points);
  if (!points.length) return [];
  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const sourceW = Math.max(1, maxX - minX);
  const sourceH = Math.max(1, maxY - minY);
  const scale = Math.min(350 / sourceW, 175 / sourceH);
  const renderedW = sourceW * scale;
  const renderedH = sourceH * scale;
  const offsetX = (520 - renderedW) / 2;
  const offsetY = 70 + (175 - renderedH) / 2;
  return geometry.map((path) => ({
    ...path,
    d: path.points.map((point, index) => {
      const x = offsetX + (point[0] - minX) * scale;
      const y = offsetY + (maxY - point[1]) * scale;
      return `${index ? "L" : "M"}${x} ${y}`;
    }).join(" ") + (path.closed ? " Z" : ""),
  }));
}

function PlanRegionsVisual({ values, geometry }: { values: LoadCalcValues; geometry?: AutomationSurfacePath[] | null }) {
  const label = textValue(values, "regionLabel", "Load region");
  const length = Math.max(1, numberValue(values, "length_ft", 28));
  const width = Math.max(1, numberValue(values, "width_ft", 18));
  const surface = textValue(values, "surfaceKind", "Plan region");
  const ratio = Math.min(2.2, Math.max(.75, length / width));
  const rectW = ratio >= 1 ? 270 : 210;
  const rectH = rectW / ratio;
  const x = (520 - rectW) / 2;
  const y = (260 - rectH) / 2;
  const enginePaths = geometry?.length ? pathGeometry(geometry) : [];
  return (
    <svg viewBox="0 0 520 300" role="img" aria-label={`${label} plan load region`}>
      <ArrowDefs />
      {enginePaths.length ? (
        <>
          {enginePaths.map((path, index) => (
            <path key={`${index}-${path.d}`} d={path.d} className={index ? "lc-svg-engine-path-secondary" : "lc-svg-engine-path"} />
          ))}
          <text x="260" y="52" className="lc-svg-note">engine-authored plan geometry</text>
          <text x="260" y="268" className="lc-svg-title">{label}</text>
        </>
      ) : (
        <>
          <rect x={x} y={y} width={rectW} height={rectH} rx="3" className="lc-svg-region" />
          <rect x={x + 18} y={y + 18} width={rectW * .42} height={rectH - 36} rx="2" className="lc-svg-region-secondary" />
          <path d={`M${x + rectW * .54} ${y + 18}V${y + rectH - 18}`} className="lc-svg-dash" />
          {Array.from({ length: 5 }).map((_, index) => {
            const ax = x + 32 + index * ((rectW - 64) / 4);
            return <path key={ax} d={`M${ax} ${y - 22}v28`} className="lc-svg-load" markerEnd="url(#lc-arrow)" />;
          })}
          <text x={x + rectW / 2} y={y + rectH / 2 - 6} className="lc-svg-title">{label}</text>
          <text x={x + rectW / 2} y={y + rectH / 2 + 13} className="lc-svg-note">{surface}</text>
          <path d={`M${x} ${y + rectH + 24}H${x + rectW}`} className="lc-svg-dimension" />
          <text x={260} y={y + rectH + 42} className="lc-svg-dimension-text">{length} ft</text>
          <path d={`M${x - 24} ${y}V${y + rectH}`} className="lc-svg-dimension" />
          <text x={x - 34} y={150} transform={`rotate(-90 ${x - 34} 150)`} className="lc-svg-dimension-text">{width} ft</text>
        </>
      )}
    </svg>
  );
}

function WindElevationVisual({ values, zones = false, unknownMissing = false }: { values: LoadCalcValues; zones?: boolean; unknownMissing?: boolean }) {
  const length = Math.max(10, numberValue(values, "buildingLength_ft", numberValue(values, "leastHorizontalDimension_ft", 30)));
  const pitch = Math.max(0, numberValue(values, "roofPitch", 6));
  const enclosure = textValue(values, "enclosureClassification", "Enclosed");
  const rawLength = values.buildingLength_ft === "" ? values.leastHorizontalDimension_ft : values.buildingLength_ft;
  const lengthLabel = unknownMissing && (rawLength === "" || rawLength === undefined) ? "—" : length;
  const heightLabel = displayNumber(values, "meanRoofHeight_ft", 22, unknownMissing);
  const roofRise = Math.min(62, 18 + pitch * 4);
  return (
    <svg viewBox="0 0 520 300" role="img" aria-label={zones ? "Components and cladding wind zones" : "MWFRS wind elevation"}>
      <ArrowDefs />
      <path d={`M105 238V118L260 ${118 - roofRise} 415 118v120z`} className="lc-svg-outline" />
      {zones ? (
        <>
          <path d="M105 118h52v120h-52zM363 118h52v120h-52z" className="lc-svg-zone-hot" />
          <path d={`M105 118 260 ${118 - roofRise} 305 ${82 - roofRise / 2} 157 118z`} className="lc-svg-zone" />
          <path d={`M363 118 260 ${118 - roofRise} 305 ${82 - roofRise / 2} 415 118z`} className="lc-svg-zone-hot" />
          <text x="131" y="182" className="lc-svg-zone-label">5</text>
          <text x="389" y="182" className="lc-svg-zone-label">5</text>
          <text x="210" y="95" className="lc-svg-zone-label">2</text>
          <text x="344" y="96" className="lc-svg-zone-label">3</text>
        </>
      ) : (
        <>
          {Array.from({ length: 6 }).map((_, index) => {
            const y = 134 + index * 18;
            return <path key={y} d={`M67 ${y}h32`} className="lc-svg-load" markerEnd="url(#lc-arrow)" />;
          })}
          <path d="M105 222h310" className="lc-svg-dash" />
          <text x="260" y="218" className="lc-svg-note">pressure surface</text>
        </>
      )}
      <path d="M105 258h310" className="lc-svg-dimension" />
      <text x="260" y="278" className="lc-svg-dimension-text">{lengthLabel} ft</text>
      <text x="260" y="27" className="lc-svg-title">{zones ? "C&C zones" : "Wind normal to ridge"}</text>
      <text x="260" y="45" className="lc-svg-note">{heightLabel} ft mean roof height · {enclosure}</text>
    </svg>
  );
}

function SnowRoofVisual({ values, unknownMissing = false }: { values: LoadCalcValues; unknownMissing?: boolean }) {
  const visualValues = { ...values, ...(firstRow(values, "roofPlanes") ?? {}), ...(firstRow(values, "adjacencies") ?? {}) };
  const pitch = Math.max(0, numberValue(visualValues, "roofPitch", 6));
  const pgLabel = displayNumber(values, "pg_psf", 30, unknownMissing);
  const pitchLabel = displayNumber(visualValues, "roofPitch", 6, unknownMissing);
  const stepLabel = displayNumber(visualValues, "stepHeight_ft", 4, unknownMissing);
  const rise = Math.min(76, 22 + pitch * 5);
  return (
    <svg viewBox="0 0 520 300" role="img" aria-label="Snow distribution across roof planes">
      <ArrowDefs />
      <path d={`M52 205 205 ${205 - rise} 332 205h136`} className="lc-svg-outline" />
      <path d="M332 205v-58h136" className="lc-svg-outline" />
      <path d={`M60 197 205 ${205 - rise + 8} 325 197`} className="lc-svg-snow" />
      <path d="M340 139h120" className="lc-svg-snow" />
      <path d="M340 139c32 7 52 24 72 58H340z" className="lc-svg-drift" />
      {Array.from({ length: 7 }).map((_, index) => {
        const x = 76 + index * 36;
        const y = x < 205 ? 198 - ((x - 60) / 145) * (rise - 8) : 128 + ((x - 205) / 120) * (rise - 8);
        return <path key={x} d={`M${x} ${y - 24}v18`} className="lc-svg-load" markerEnd="url(#lc-arrow)" />;
      })}
      <text x="205" y="42" className="lc-svg-title">Balanced + unbalanced snow</text>
      <text x="205" y="61" className="lc-svg-note">pg {pgLabel} psf · {pitchLabel}:12 roof</text>
      <text x="410" y="122" className="lc-svg-note">drift at {stepLabel} ft step</text>
      <text x="392" y="185" className="lc-svg-zone-label">drift</text>
      <path d="M52 242h416" className="lc-svg-dimension" />
      <text x="260" y="264" className="lc-svg-dimension-text">roof planes and adjacency</text>
    </svg>
  );
}

function SeismicStoriesVisual({ values, unknownMissing = false }: { values: LoadCalcValues; unknownMissing?: boolean }) {
  const storyRows = Array.isArray(values.stories) ? values.stories : [];
  const axisRows = Array.isArray(values.axes) ? values.axes : [];
  const count = Math.max(1, Math.min(8, storyRows.length || Math.round(numberValue(values, "storyCount", 3))));
  const axis = axisRows.length ? String(axisRows[0].axis ?? "X") : textValue(values, "axis", "X");
  const countLabel = storyRows.length || displayNumber(values, "storyCount", 3, unknownMissing);
  const tableHeight = storyRows.reduce((maximum, row) => Math.max(maximum, Number(row.height_ft) || 0), 0);
  const heightLabel = tableHeight || displayNumber(values, "totalHeight_ft", count * 10, unknownMissing);
  const storyH = 160 / count;
  return (
    <svg viewBox="0 0 520 300" role="img" aria-label="ELF story force distribution">
      <ArrowDefs />
      <path d="M150 235h235M170 235V62M365 235V62" className="lc-svg-outline" />
      {Array.from({ length: count }).map((_, index) => {
        const y = 235 - (index + 1) * storyH;
        const arrow = 54 + (index + 1) * 15;
        return (
          <g key={y}>
            <path d={`M170 ${y}h195`} className="lc-svg-floor" />
            <path d={`M${170 - arrow} ${y}h${arrow - 7}`} className="lc-svg-load" markerEnd="url(#lc-arrow)" />
            <text x={158 - arrow} y={y + 4} className="lc-svg-note">F{index + 1}</text>
            <text x="378" y={y + 4} className="lc-svg-note">Level {index + 1}</text>
          </g>
        );
      })}
      <path d="M170 255h195" className="lc-svg-dimension" />
      <text x="268" y="276" className="lc-svg-dimension-text">ELF axis {axis}</text>
      <text x="270" y="28" className="lc-svg-title">Story force distribution</text>
      <text x="270" y="47" className="lc-svg-note">{countLabel} stories · {heightLabel} ft total height</text>
    </svg>
  );
}

function AtticSectionVisual({ values, unknownMissing = false }: { values: LoadCalcValues; unknownMissing?: boolean }) {
  const storage = Boolean(values.storageEnabled);
  const widthLabel = displayNumber(values, "ceilingWidth_ft", 28, unknownMissing);
  const pitchLabel = displayNumber(values, "roofPitch", 6, unknownMissing);
  const thresholdLabel = displayNumber(values, "headroomThreshold_in", 30, unknownMissing);
  return (
    <svg viewBox="0 0 520 300" role="img" aria-label="Attic storage headroom extent">
      <ArrowDefs />
      <path d="M65 220h390M65 220 260 62 455 220" className="lc-svg-outline" />
      <path d="M126 220 260 111 394 220z" className={storage ? "lc-svg-region" : "lc-svg-region-secondary"} />
      <path d="M126 220v-39M394 220v-39" className="lc-svg-dash" />
      <path d="M126 246h268" className="lc-svg-dimension" />
      <text x="260" y="267" className="lc-svg-dimension-text">storage extent where clear height ≥ {thresholdLabel} in</text>
      <text x="260" y="28" className="lc-svg-title">Ceiling and attic live-load regions</text>
      <text x="260" y="47" className="lc-svg-note">{widthLabel} ft ceiling · {pitchLabel}:12 roof</text>
      <text x="260" y="177" className="lc-svg-zone-label">attic storage</text>
      <text x="95" y="212" className="lc-svg-note">base only</text>
      <text x="425" y="212" className="lc-svg-note">base only</text>
    </svg>
  );
}

function DrainageVisual({ values, unknownMissing = false }: { values: LoadCalcValues; unknownMissing?: boolean }) {
  const drains = Math.max(1, Math.min(4, Math.round(numberValue(values, "drainCount", 2))));
  const lengthLabel = displayNumber(values, "length_ft", 36, unknownMissing);
  const widthLabel = displayNumber(values, "width_ft", 24, unknownMissing);
  const drainsLabel = displayNumber(values, "drainCount", 2, unknownMissing);
  return (
    <svg viewBox="0 0 520 300" role="img" aria-label="Roof drainage and rain load conditions">
      <ArrowDefs />
      <path d="M85 70h350v160H85z" className="lc-svg-outline" />
      <path d="M85 70h350v160H85z" className="lc-svg-water" />
      {Array.from({ length: drains }).map((_, index) => {
        const x = 145 + index * (230 / Math.max(1, drains - 1));
        return <g key={x}><circle cx={x} cy="150" r="12" className="lc-svg-drain" /><path d={`M${x - 5} 150h10M${x} 145v10`} className="lc-svg-floor" /></g>;
      })}
      {Array.from({ length: 5 }).map((_, index) => <path key={index} d={`M${118 + index * 70} 38v24`} className="lc-svg-load" markerEnd="url(#lc-arrow)" />)}
      <path d="M85 252h350" className="lc-svg-dimension" />
      <text x="260" y="274" className="lc-svg-dimension-text">{lengthLabel} × {widthLabel} ft roof region</text>
      <text x="260" y="24" className="lc-svg-title">Rain and drainage condition</text>
      <text x="260" y="151" className="lc-svg-zone-label">{drainsLabel} primary drains</text>
    </svg>
  );
}

function WallVisual({ values, unknownMissing = false }: { values: LoadCalcValues; unknownMissing?: boolean }) {
  const left = numberValue(values, "heightLeft_ft", 9);
  const right = numberValue(values, "heightRight_ft", 9);
  const delta = Math.max(-70, Math.min(70, (right - left) * 12));
  const lengthLabel = displayNumber(values, "length_ft", 30, unknownMissing);
  const leftLabel = displayNumber(values, "heightLeft_ft", 9, unknownMissing);
  const rightLabel = displayNumber(values, "heightRight_ft", 9, unknownMissing);
  const openingRows = Array.isArray(values.openings) ? values.openings : [];
  const scheduledOpeningArea = openingRows.reduce((sum, row) => sum + (Number(row.width_ft) || 0) * (Number(row.height_ft) || 0) * (Number(row.count) || 1), 0);
  const openingLabel = scheduledOpeningArea || displayNumber(values, "openingArea_sqft", 36, unknownMissing);
  return (
    <svg viewBox="0 0 520 300" role="img" aria-label="Wall gravity load elevation">
      <ArrowDefs />
      <path d={`M80 225V78L440 ${78 - delta}v${147 + delta}z`} className="lc-svg-region" />
      <rect x="210" y="130" width="100" height="95" className="lc-svg-opening" />
      {Array.from({ length: 8 }).map((_, index) => {
        const x = 96 + index * 46;
        return <path key={x} d={`M${x} 244v-11`} className="lc-svg-load-up" markerEnd="url(#lc-arrow)" />;
      })}
      <text x="260" y="27" className="lc-svg-title">Wall-face gravity distribution</text>
      <text x="260" y="47" className="lc-svg-note">{leftLabel}–{rightLabel} ft high · {openingLabel} ft² openings</text>
      <path d="M80 257h360" className="lc-svg-dimension" />
      <text x="260" y="279" className="lc-svg-dimension-text">{lengthLabel} ft wall length</text>
      <text x="260" y="120" className="lc-svg-zone-label">net wall face</text>
    </svg>
  );
}

export function LoadVisual({ template, values, geometry, unknownMissing = false }: { template: LoadCalcTemplate; values: LoadCalcValues; geometry?: AutomationSurfacePath[] | null; unknownMissing?: boolean }) {
  switch (template.visual) {
    case "wind-elevation": return <WindElevationVisual values={values} unknownMissing={unknownMissing} />;
    case "wind-zones": return <WindElevationVisual values={values} zones unknownMissing={unknownMissing} />;
    case "snow-roof": return <SnowRoofVisual values={values} unknownMissing={unknownMissing} />;
    case "seismic-stories": return <SeismicStoriesVisual values={values} unknownMissing={unknownMissing} />;
    case "attic-section": return <AtticSectionVisual values={values} unknownMissing={unknownMissing} />;
    case "drainage": return <DrainageVisual values={values} unknownMissing={unknownMissing} />;
    case "wall-elevation": return <WallVisual values={values} unknownMissing={unknownMissing} />;
    default: return <PlanRegionsVisual values={values} geometry={geometry} />;
  }
}

// Engine result-table cells arrive verbatim; snake_case identifiers
// ("roof_surface", "wall_sheathing") humanize at render — numbers and real
// strings pass through untouched.
const SNAKE_IDENTIFIER = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;
function displayResultCell(stored: unknown): string {
  const text = String(stored);
  if (typeof stored === "string" && SNAKE_IDENTIFIER.test(stored)) {
    const spaced = stored.replace(/_/g, " ");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }
  return text;
}

function StatusBadge({ payload, emptyLabel = "Not connected" }: { payload?: LoadCalcResultPayload; emptyLabel?: string }) {
  if (!payload || payload.status === "not_run") return <span className="lc-status is-neutral"><i aria-hidden="true" />{emptyLabel}</span>;
  if (payload.status === "not_applicable") return <span className="lc-status is-neutral"><i aria-hidden="true" />Not applicable</span>;
  if (payload.status === "complete") return <span className="lc-status is-pass"><i aria-hidden="true" />Calculated</span>;
  if (payload.status === "blocked") return <span className="lc-status is-fail"><i aria-hidden="true" />Blocked</span>;
  return <span className="lc-status is-near"><i aria-hidden="true" />Partial</span>;
}

export function LoadCalcWorkspace({
  templateId: templateIdProp,
  result,
  initialValues,
  planGeometry,
  automation = false,
  viewerDxfRef,
}: {
  templateId?: string;
  result?: LoadCalcResultPayload;
  initialValues?: LoadCalcValues;
  planGeometry?: AutomationSurfacePath[] | null;
  automation?: boolean;
  viewerDxfRef?: string | null;
}) {
  const params = useParams<{ id: string; templateId: string; sheetId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { project, sheets, setSheets } = useCalcProject();
  const { openExport } = useCalcExport();
  const projectId = params.id ?? "";
  const persistedSheetId = params.sheetId ?? null;
  const contextSheet = persistedSheetId
    ? sheets.find((candidate) => candidate.id === persistedSheetId) ?? null
    : null;
  const [activeSheet, setActiveSheet] = useState<CalcSheet | null>(contextSheet);
  const [sheetLoading, setSheetLoading] = useState(Boolean(persistedSheetId));
  const [name, setName] = useState(contextSheet?.name ?? (automation ? "AUTO" : "Draft"));
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [assemblyOpen, setAssemblyOpen] = useState(false);
  const sheetTemplateId = loadTemplateIdFromCalculatorId(activeSheet?.calculatorId ?? contextSheet?.calculatorId);
  const rawTemplateId = templateIdProp ?? params.templateId ?? sheetTemplateId ?? undefined;
  const templateId = isLoadCalcTemplateId(rawTemplateId) ? rawTemplateId : "dead_gravity";
  const template = LOAD_CALC_TEMPLATE_BY_ID[templateId];
  const storedValues = activeSheet?.inputs as LoadCalcValues | undefined;
  const resolvedInitialValues = useMemo(() => {
    const supplied = upgradeLegacyLoadValues(template.id, persistedSheetId ? storedValues : initialValues);
    if (!automation) return { ...defaultLoadCalcValues(template), ...supplied };
    const empty: LoadCalcValues = {};
    for (const section of template.inputs) {
      for (const field of section.fields) empty[field.key] = "";
    }
    return { ...empty, ...supplied };
  }, [automation, initialValues, persistedSheetId, storedValues, template]);
  const [values, setValues] = useState<LoadCalcValues>(() => resolvedInitialValues);
  const [activeCase, setActiveCase] = useState(template.cases[0]);
  const [activeResult, setActiveResult] = useState(template.results[0]?.id ?? "");
  const [dirty, setDirty] = useState(false);
  const valuesRef = useRef<LoadCalcValues>(resolvedInitialValues);
  const nameRef = useRef(name);
  const revisionRef = useRef(0);
  const savedRevisionRef = useRef(0);
  const inputRevisionRef = useRef(0);
  const savedInputRevisionRef = useRef(0);
  const versionRef = useRef<number | undefined>(contextSheet?.version);
  const savingRef = useRef(false);
  const queuedSaveRef = useRef(false);
  // Auto-calculation bookkeeping: runs are keyed to the input revision so a
  // no-change blur never re-hits the engine, and edits landing while a run is
  // in flight queue exactly one trailing run (the loop in calculateSheet).
  const calculatingRef = useRef(false);
  const queuedCalcRef = useRef(false);
  const calculatedInputRevisionRef = useRef(0);
  const storedResult = activeSheet?.results?.structured?.loadCalculation as LoadCalcResultPayload | undefined;
  const displayResult = dirty && !automation ? undefined : (result ?? storedResult);
  const wallAssembly = Array.isArray(values.wallAssembly)
    ? values.wallAssembly as WallAssemblyLayer[]
    : [];

  useEffect(() => {
    if (!persistedSheetId || !projectId) {
      setSheetLoading(false);
      return;
    }
    let alive = true;
    setSheetLoading(true);
    getCalcSheet(projectId, persistedSheetId)
      .then(({ calculation }) => {
        if (!alive) return;
        if (!loadTemplateIdFromCalculatorId(calculation.calculatorId)) {
          throw new Error("This sheet is not a load calculation.");
        }
        setActiveSheet(calculation);
        versionRef.current = calculation.version;
        nameRef.current = calculation.name;
        setName(calculation.name);
      })
      .catch((error) => {
        if (!alive) return;
        toast(error instanceof Error ? error.message : "Failed to load calculation", "error");
        navigate(`/app/calculations/${projectId}`, { replace: true });
      })
      .finally(() => alive && setSheetLoading(false));
    return () => { alive = false; };
  }, [navigate, persistedSheetId, projectId, toast]);

  useEffect(() => {
    valuesRef.current = resolvedInitialValues;
    revisionRef.current = 0;
    savedRevisionRef.current = 0;
    inputRevisionRef.current = 0;
    savedInputRevisionRef.current = 0;
    setValues(resolvedInitialValues);
    setActiveCase(template.cases[0]);
    setActiveResult(template.results[0]?.id ?? "");
    setDirty(false);
  }, [resolvedInitialValues, template]);

  const currentResultTable = useMemo(
    () => template.results.find((table) => table.id === activeResult) ?? template.results[0],
    [activeResult, template.results],
  );
  const currentResultRows = useMemo(() => {
    if (!currentResultTable) return [];
    const engineRows = displayResult?.tables?.[currentResultTable.id];
    if (engineRows?.length) return engineRows;
    const firstColumn = currentResultTable.columns[0]?.key;
    return currentResultTable.rowLabels.map((label) => firstColumn ? { [firstColumn]: label } : {});
  }, [currentResultTable, displayResult?.tables]);
  const assumptionItems = automation ? (displayResult?.assumptions ?? []) : (displayResult?.assumptions ?? template.assumptions);
  const deferredItems = automation ? (displayResult?.deferred ?? []) : (displayResult?.deferred ?? template.deferred);
  const noticeItems: CalcSheetNotice[] = [
    ...(displayResult?.warnings ?? []).map((item, index) => ({
      key: `warning-${index}`,
      message: item,
      tone: "warn" as const,
    })),
    ...assumptionItems.map((item, index) => ({
      key: `assumption-${index}`,
      message: item,
      tone: "note" as const,
    })),
    ...(displayResult?.notes ?? []).map((item, index) => ({
      key: `note-${index}`,
      message: item,
      tone: "note" as const,
    })),
  ];
  const deferredNotices: CalcSheetNotice[] = deferredItems.map((item, index) => ({
    key: `deferred-${index}`,
    message: item,
    tone: "warn",
  }));
  const sourceNotices: CalcSheetNotice[] = (displayResult?.sources ?? [template.standard]).map((item, index) => ({
    key: `source-${index}`,
    message: item,
    tone: "note",
  }));

  const updateValue = (key: string, value: LoadCalcValue) => {
    valuesRef.current = { ...valuesRef.current, [key]: value };
    revisionRef.current += 1;
    inputRevisionRef.current += 1;
    setValues(valuesRef.current);
    setDirty(true);
  };

  const updateValues = (patch: Record<string, LoadCalcValue>) => {
    valuesRef.current = { ...valuesRef.current, ...patch };
    revisionRef.current += 1;
    inputRevisionRef.current += 1;
    setValues(valuesRef.current);
    setDirty(true);
  };

  const updateName = (nextName: string) => {
    nameRef.current = nextName;
    revisionRef.current += 1;
    setName(nextName);
    setDirty(true);
  };

  const commitValue = (key: string, value?: LoadCalcValue) => {
    if (value !== undefined && valuesRef.current[key] !== value) updateValue(key, value);
    autoCalculate();
  };

  async function saveDraft(): Promise<CalcSheet | null> {
    if (!persistedSheetId || automation) return null;
    if (savingRef.current) {
      queuedSaveRef.current = true;
      return null;
    }
    if (revisionRef.current === savedRevisionRef.current) return activeSheet;
    savingRef.current = true;
    setSaving(true);
    let latestCalculation = activeSheet;
    try {
      do {
        queuedSaveRef.current = false;
        const committedRevision = revisionRef.current;
        const committedInputRevision = inputRevisionRef.current;
        const committedName = nameRef.current.trim() || template.shortName;
        const payload: {
          name: string;
          inputs?: CalcInputs;
          expectedVersion?: number;
        } = {
          name: committedName,
          expectedVersion: versionRef.current,
        };
        if (committedInputRevision !== savedInputRevisionRef.current) {
          payload.inputs = { ...valuesRef.current } as CalcInputs;
        }
        const { calculation } = await updateCalcSheet(projectId, persistedSheetId, payload);
        latestCalculation = calculation;
        versionRef.current = calculation.version;
        setActiveSheet(calculation);
        nameRef.current = calculation.name;
        setName(calculation.name);
        setSheets((current) => current.map((candidate) => (
          candidate.id === calculation.id ? calculation : candidate
        )));
        savedRevisionRef.current = committedRevision;
        savedInputRevisionRef.current = committedInputRevision;
        if (revisionRef.current === committedRevision) setDirty(false);
      } while (queuedSaveRef.current);
      return latestCalculation;
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to save load calculation", "error");
      return null;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  // quiet: an auto-run (field exit) — status + result panels communicate the
  // outcome, so no toasts; the explicit header Recalculate keeps them.
  async function calculateSheet({ quiet = false }: { quiet?: boolean } = {}) {
    if (!persistedSheetId || automation) return;
    if (calculatingRef.current || savingRef.current) {
      queuedCalcRef.current = true;
      return;
    }
    calculatingRef.current = true;
    setCalculating(true);
    try {
      do {
        queuedCalcRef.current = false;
        const committedInputRevision = inputRevisionRef.current;
        const needsSave = revisionRef.current !== savedRevisionRef.current;
        const draft = needsSave ? await saveDraft() : activeSheet;
        if (!draft) throw new Error("Calculation sheet is unavailable.");
        const { calculation } = await calculateLoadCalcSheet(projectId, persistedSheetId, draft.version);
        versionRef.current = calculation.version;
        setActiveSheet(calculation);
        nameRef.current = calculation.name;
        setName(calculation.name);
        setSheets((current) => current.map((candidate) => candidate.id === calculation.id ? calculation : candidate));
        setDirty(false);
        calculatedInputRevisionRef.current = committedInputRevision;
        if (!quiet) {
          const calcStatus = calculation.results?.structured?.loadCalculation?.status;
          toast(calcStatus === "partial" ? `Calculated ${calculation.name} with deferred checks` : `Calculated ${calculation.name}`, calcStatus === "blocked" ? "error" : "success");
        }
      } while (queuedCalcRef.current);
    } catch (error) {
      if (!quiet) toast(error instanceof Error ? error.message : "Failed to calculate load", "error");
    } finally {
      calculatingRef.current = false;
      setCalculating(false);
    }
  }

  // Every calculator computes on field exit (autocalc on blur) — the engine
  // run is never gated behind a manual button. No-change blurs skip the
  // engine; the header Recalculate remains as an explicit re-run affordance.
  const autoCalculate = () => {
    if (!persistedSheetId || automation) return;
    if (inputRevisionRef.current === calculatedInputRevisionRef.current) {
      void saveDraft();
      return;
    }
    void calculateSheet({ quiet: true });
  };

  async function removeDraft() {
    if (!persistedSheetId || deleting) return;
    setDeleting(true);
    try {
      await deleteCalcSheet(projectId, persistedSheetId);
      setSheets((current) => current.filter((candidate) => candidate.id !== persistedSheetId));
      navigate(`/app/calculations/${projectId}`, { replace: true });
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to delete load calculation", "error");
      setDeleting(false);
      setDeleteOpen(false);
    }
  }

  let headerStatus: { label: string; tone: CalcSheetTone };
  if (calculating) headerStatus = { label: "Calculating…", tone: "idle" };
  else if (saving) headerStatus = { label: "Saving…", tone: "idle" };
  else if (dirty) headerStatus = { label: "Pending", tone: "near" };
  else if (displayResult?.status === "complete") headerStatus = { label: "Calculated", tone: "pass" };
  else if (displayResult?.status === "blocked") headerStatus = { label: "Blocked", tone: "fail" };
  else if (displayResult?.status === "partial") headerStatus = { label: "Partial", tone: "near" };
  else if (displayResult?.status === "not_applicable") headerStatus = { label: "Not applicable", tone: "idle" };
  else if (persistedSheetId) headerStatus = { label: "Draft", tone: "idle" };
  else headerStatus = { label: automation ? "Not connected" : "Preview", tone: "idle" };

  if (sheetLoading) {
    return <div className="nds-workspace lc-workspace"><div className="nds-loading">Loading calculation…</div></div>;
  }

  return (
    <div className="nds-workspace lc-workspace calc-scope" data-load-template={template.id}>
      <CalcSheetHeader
        mark={name}
        onMark={automation ? undefined : updateName}
        onMarkCommit={persistedSheetId && !automation ? () => void saveDraft() : undefined}
        calculator={template.name}
        standard={template.standard}
        status={headerStatus.label}
        statusTone={headerStatus.tone}
        note={automation ? "Automation result" : dirty ? "Recalculates on field exit" : template.family}
        readOnly={automation || calculating}
        actions={!automation && persistedSheetId ? (
          <>
            <button type="button" className="nds-btn-secondary" disabled={saving || calculating} onClick={() => void calculateSheet()}>
              {calculating ? "Working…" : (
                <><span className="nds-action-long">Recalculate</span><span className="nds-action-short">Recalc</span></>
              )}
            </button>
            <HelpTip
              label="Calculation export"
              text="Export this calculation as a Simplified or Detailed PDF after a preflight review."
              topic="calc-export#scope"
            />
            <button type="button" className="nds-topbar-export" data-helpshot="export-calculation" disabled={calculating} onClick={() => openExport({ sheetId: persistedSheetId })}>
              Export
            </button>
            <button type="button" className="nds-btn-ghost" disabled={calculating} onClick={() => setDeleteOpen(true)}>
              Delete
            </button>
          </>
        ) : undefined}
      />

      <div className="nds-panes lc-panes">
        <aside className="nds-pane-left lc-input-pane" aria-label="Calculation inputs" data-helpshot="load-inputs">
          {template.id === "wall_gravity" ? (
            <div className="catalog-selection-card wall-assembly-card" data-helpshot="wall-assembly-entry">
              <header>
                <span>
                  Wall assembly database
                  <HelpTip
                    label="Wall assembly database"
                    text="Builds a wall face-weight ledger from stable product keys and explicit manual layers. The API re-resolves catalog keys before every calculation."
                    topic="wall-assembly"
                  />
                </span>
                <div>
                  {wallAssembly.length > 0 ? <button type="button" disabled={automation || calculating} onClick={() => {
                    updateValues({ wallAssembly: [], unitWeight_psf: 8 });
                    autoCalculate();
                  }}>Clear</button> : null}
                  <button type="button" disabled={automation || calculating} onClick={() => setAssemblyOpen(true)}>{wallAssembly.length > 0 ? "Edit assembly" : "Build assembly"}</button>
                </div>
              </header>
              <strong>{wallAssembly.length > 0
                ? `${wallAssembly.length} layer${wallAssembly.length === 1 ? "" : "s"} · ${Number(values.unitWeight_psf ?? 0).toFixed(3)} psf`
                : "Manual wall face weight"}</strong>
              <small>{wallAssembly.length > 0
                ? "Saved product keys are re-resolved by the API before every calculation."
                : "Build a sourced layer ledger, or continue with the explicit manual face weight below."}</small>
            </div>
          ) : null}
          <div className="nds-inputs lc-input-sections">
            {template.inputs.map((section, index) => (
              <CalcInputCard
                letter={String.fromCharCode(65 + index)}
                title={section.label}
                hint={section.summary}
                defaultOpen={section.openByDefault}
                key={section.id}
              >
                <div className="lc-section-body">
                  {section.fields.map((field) => (
                    <FieldControl
                      key={field.key}
                      field={field}
                      value={values[field.key]}
                      disabled={automation || calculating || (
                        template.id === "wall_gravity"
                        && field.key === "unitWeight_psf"
                        && wallAssembly.length > 0
                      )}
                      onChange={(value) => updateValue(field.key, value)}
                      onCommit={(value) => commitValue(field.key, value)}
                    />
                  ))}
                </div>
              </CalcInputCard>
            ))}
          </div>

          <section className="nds-design-basis lc-source-contract">
            <span>
              Source contract
              <HelpTip
                label="Manual load calculation lifecycle"
                text="A field exit saves changed inputs and starts an authoritative load-engine run. Recalculate repeats the run explicitly and reports a refusal if the engine rejects the input."
                topic="load-calcs#commit-and-calculate"
              />
            </span>
            <strong>{template.sourceContract}</strong>
            <small>{automation
              ? "Read-only engine handoff. Missing fields remain blank."
              : "Inputs save and recalculate on field exit through the authoritative engine. Results remain nominal and unfactored."}</small>
          </section>
        </aside>

        <main className="nds-pane-right lc-results-pane">
          {/* Report-cover identity block (print-only, reference parity). Load
              calculators have no ASD/LRFD choice — the method line stays off;
              the registry-declared standard names the basis. */}
          <NdsCoverBlock
            project={project}
            calculatorId={activeSheet?.calculatorId ?? `load_${template.id}`}
            memberMark={name}
            roleLabel={null}
            edition={template.standard ?? null}
            designMethod={null}
            updatedAt={activeSheet?.updatedAt ?? null}
            peStamped={activeSheet?.peStamped ?? false}
          />
          <CalcResultPanel
            title="Summary"
            hint={template.family}
            ariaLabel="Calculation summary"
            className="lc-summary-panel"
            actions={(
              <HelpTip
                label="Load calculation status"
                text="Status reports calculation completeness. Load generators publish actions and provenance, not a member pass/fail verdict or demand-capacity ratio."
                topic="load-calc-results#status-and-governing"
              />
            )}
          >
            <CalcMetricStrip items={[
              {
                key: "status",
                label: "Status",
                value: <StatusBadge payload={displayResult} emptyLabel={headerStatus.label} />,
                tone: headerStatus.tone,
              },
              { key: "governing", label: "Governing", value: displayResult?.governingLabel ?? "—" },
              { key: "value", label: "Value", value: displayResult?.governingValue ?? "—", mono: true },
              { key: "cases", label: "Cases", value: template.cases.length, mono: true },
              { key: "source", label: "Source", value: automation ? "Automation" : displayResult ? "Engine result" : dirty ? "Changed inputs" : "Draft inputs" },
            ]} />
          </CalcResultPanel>

          <CalcResultPanel
            title="Geometry & load cases"
            hint="input-driven explanatory view"
            className="lc-geometry-panel"
            actions={(
              <div className="lc-case-tabs" role="group" aria-label="Load cases">
                {template.cases.map((caseName) => (
                  <button
                    type="button"
                    key={caseName}
                    className={activeCase === caseName ? "is-active" : ""}
                    aria-pressed={activeCase === caseName}
                    onClick={() => setActiveCase(caseName)}
                  >
                    {caseName}
                  </button>
                ))}
              </div>
            )}
          >
            <div className="lc-visual-body" id="load-case-visual" role="group" aria-label={`${activeCase} visual`}>
              <div className="lc-svg-stage">
                <LoadVisual template={template} values={values} geometry={planGeometry} unknownMissing={automation} />
              </div>
            </div>
            <footer className="calc-sheet-panel-foot">
              {activeCase} · geometry follows current inputs; diagram arrows are explanatory.
              {viewerDxfRef ? <> · Review DXF source: <span className="num">{viewerDxfRef}</span></> : null}
            </footer>
          </CalcResultPanel>

          <CalcResultPanel title="Calculation trace" hint="intermediate values" className="lc-trace-panel">
            <div className="lc-trace-grid">
              {template.intermediates.map((label) => {
                const value = displayResult?.intermediates?.[label];
                return <div key={label}><span>{label}</span><strong className="num">{value == null ? "—" : String(value)}</strong></div>;
              })}
            </div>
            {!displayResult ? <p className="calc-sheet-panel-foot">{dirty ? "Inputs changed. Recalculate to replace the previous result." : "Intermediate values will appear here exactly as returned by the calculation engine."}</p> : null}
          </CalcResultPanel>

          {currentResultTable ? (
            <CalcResultPanel
              title={currentResultTable.label}
              hint={currentResultTable.description}
              className="lc-result-table-panel"
              actions={template.results.length > 1 ? (
                <select
                  className="calc-sheet-panel-select"
                  value={activeResult}
                  onChange={(event) => setActiveResult(event.target.value)}
                >
                  {template.results.map((table) => <option key={table.id} value={table.id}>{table.label}</option>)}
                </select>
              ) : null}
            >
              <div className="calc-sheet-table-scroll">
                <table className="calc-sheet-table">
                  <thead><tr>{currentResultTable.columns.map((column, columnIndex) => <th key={column.key} className={columnIndex > 1 ? "num" : undefined}>{column.label}{column.unit ? <small>{column.unit}</small> : null}</th>)}</tr></thead>
                  <tbody>
                    {currentResultRows.map((row, rowIndex) => (
                      <tr key={`${rowIndex}-${String(row[currentResultTable.columns[0]?.key] ?? "row")}`}>
                        {currentResultTable.columns.map((column, columnIndex) => {
                          const stored = row[column.key];
                          return <td key={column.key} className={columnIndex > 1 ? "num" : undefined}>{stored == null ? "—" : displayResultCell(stored)}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!displayResult ? <footer className="calc-sheet-panel-foot">{dirty ? "The stored result is hidden because the inputs changed." : "Result rows are intentionally empty until an engine-authored result is available."}</footer> : null}
            </CalcResultPanel>
          ) : null}

          <CalcDisclosure
            title="Warnings & assumptions"
            hint="prints on the sheet"
            count={noticeItems.length}
          >
            {noticeItems.length ? (
              <CalcNoticeList items={noticeItems} />
            ) : (
              <p className="calc-sheet-empty-note">
                {automation
                  ? "No assumptions, panel failures, or engine warnings were included in this manifest."
                  : "No engine assumptions or warnings are available before calculation."}
              </p>
            )}
          </CalcDisclosure>

          <CalcDisclosure title="Deferred & unsupported" count={deferredNotices.length}>
            {deferredNotices.length ? (
              <CalcNoticeList items={deferredNotices} />
            ) : (
              <p className="calc-sheet-empty-note">No deferred conditions are recorded.</p>
            )}
          </CalcDisclosure>

          <CalcDisclosure title="Sources" count={sourceNotices.length}>
            <CalcNoticeList items={sourceNotices} />
          </CalcDisclosure>
        </main>
      </div>
      <ConfirmDialog
        open={deleteOpen}
        title="Delete calculation?"
        message={`Delete ${name || template.shortName}? This cannot be undone.`}
        confirmLabel={deleting ? "Deleting…" : "Delete calculation"}
        danger
        onConfirm={() => void removeDraft()}
        onCancel={() => setDeleteOpen(false)}
      />
      {assemblyOpen ? (
        <WallAssemblyPicker
          initialLayers={wallAssembly}
          onClose={() => setAssemblyOpen(false)}
          onApply={(layers, previewWeightPsf) => {
            updateValues({
              wallAssembly: layers as LoadCalcRow[],
              unitWeight_psf: previewWeightPsf,
            });
            setAssemblyOpen(false);
            autoCalculate();
          }}
        />
      ) : null}
    </div>
  );
}
